import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, ChevronRight, Sparkles } from 'lucide-react';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import {
  getMockCrossSellOpportunities,
  PRODUCT_LABELS,
  PRODUCT_TYPES,
  type ProductType,
  type CrossSellOpportunity,
} from '../lib/mock-cross-sell';

interface CrossSellOpportunityResponse {
  product: string;
  opportunities: CrossSellOpportunity[];
  total: number;
}

// ---- ScoreBar ----

function ScoreBar({ score }: { score: number }): React.ReactElement {
  const tierCls = score >= 70 ? 'bg-running' : score >= 40 ? 'bg-brand' : 'bg-line-strong';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-sunken rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${tierCls}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-sm font-semibold text-ink tabular-nums">{score}%</span>
    </div>
  );
}

// ---- TierBadge ----

function TierBadge({ tier }: { tier: CrossSellOpportunity['propensityTier'] }): React.ReactElement {
  const cls =
    tier === 'high'
      ? 'bg-pass-soft text-pass'
      : tier === 'medium'
        ? 'bg-warn-soft text-warn'
        : 'bg-sunken text-ink-soft';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {tier}
    </span>
  );
}

// ---- CrossSellPage ----

export function CrossSellPage(): React.ReactElement {
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState<ProductType>('heloc');

  const [opportunities, setOpportunities] = useState<CrossSellOpportunity[]>(
    () => getMockCrossSellOpportunities(selectedProduct),
  );

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch(
          `/api/cross-sell/opportunities?product=${selectedProduct}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('crm_dev_token') ?? ''}`,
              'x-tenant-id': 'tenant-cascade',
              'x-staff-id': 'staff-001',
              'x-staff-role': 'loan_officer',
            },
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as CrossSellOpportunityResponse;
        setOpportunities(data.opportunities);
      } catch {
        setOpportunities(getMockCrossSellOpportunities(selectedProduct));
      }
    })();
  }, [selectedProduct]);

  return (
    <div className="flex flex-col gap-5">
      {/* Page header */}
      <div>
        <SectionHeader title="Cross-Sell Opportunities" />
        <p className="text-sm text-ink-muted mt-1">
          Members ranked by AI propensity score for selected product
        </p>
      </div>

      {/* Product filter tabs */}
      <div className="flex gap-2 mb-6">
        {PRODUCT_TYPES.map((product) => (
          <button
            key={product}
            type="button"
            onClick={() => setSelectedProduct(product)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedProduct === product
                ? 'bg-brand text-on-brand'
                : 'bg-white text-ink-soft hover:bg-bg'
            }`}
          >
            {PRODUCT_LABELS[product]}
          </button>
        ))}
      </div>

      {/* Results summary */}
      <p className="text-sm text-ink-muted mb-4">
        {opportunities.length} members with {PRODUCT_LABELS[selectedProduct]} propensity scores
      </p>

      {/* Empty state or opportunity list */}
      {opportunities.length === 0 ? (
        <EmptyState
          icon={TrendingUp}
          headline="No opportunities"
          body="No members have propensity scores for this product yet."
        />
      ) : (
        <div className="bg-white rounded-xl border border-line shadow-sm overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-[2rem_1fr_11rem_1fr_9rem_2rem] gap-4 px-4 py-2 bg-bg border-b border-sunken text-xs font-medium text-ink-muted uppercase tracking-wide">
            <span>#</span>
            <span>Member</span>
            <span>Score</span>
            <span>Why</span>
            <span>Stage</span>
            <span />
          </div>

          {/* Opportunity rows */}
          <div className="divide-y divide-sunken">
            {opportunities.map((opp, index) => (
              <button
                key={opp.memberId}
                type="button"
                onClick={() => navigate(`/members/${opp.memberId}`)}
                className="w-full flex items-center gap-4 px-4 py-3 hover:bg-bg rounded-lg transition-colors border-b border-sunken text-left"
              >
                {/* Rank */}
                <span className="w-6 flex-shrink-0 text-xs text-ink-muted font-medium tabular-nums text-right">
                  {index + 1}
                </span>

                {/* Member identity */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">{opp.memberName}</p>
                  <p className="text-xs text-ink-muted">#{opp.memberNumber}</p>
                </div>

                {/* Score bar + tier */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ScoreBar score={opp.propensityScore} />
                  <TierBadge tier={opp.propensityTier} />
                </div>

                {/* SHAP factor chip */}
                <div className="flex-1 min-w-0">
                  {opp.topShapFactor !== null ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-running-soft text-running-ink">
                      <Sparkles className="h-3 w-3" />
                      {opp.topShapFactor}
                    </span>
                  ) : (
                    <span className="text-ink-muted">—</span>
                  )}
                </div>

                {/* Lifecycle stage badge */}
                <div className="flex-shrink-0">
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-sunken text-ink-soft">
                    {opp.lifecycleStage.charAt(0).toUpperCase() + opp.lifecycleStage.slice(1).replace('_', ' ')}
                  </span>
                </div>

                {/* Arrow */}
                <ChevronRight className="h-4 w-4 text-ink-faint flex-shrink-0" aria-hidden />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
